import React, { useEffect, useState, useMemo } from "react";
import { api, exportCSV, money, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/CrudModule";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus as PlusIcon, DownloadSimple as DownloadSimpleIcon, MagnifyingGlass as MagnifyingGlassIcon, PencilSimple as PencilSimpleIcon, BookOpen as BookOpenIcon, CurrencyDollar as CurrencyDollarIcon, Warning as WarningIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPES = ["Residential", "Commercial", "Industrial", "Hotel", "Restaurant", "Government"];
const emptyForm = { name: "", type: "Residential", phone: "", email: "", address: "", town: "", credit_limit: 0, balance: 0, status: "active", emergency_contact: "", notes: "" };

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [credit, setCredit] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [payOpen, setPayOpen] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const [pay, setPay] = useState({ amount: "", type: "payment", method: "cash", note: "" });
  const [stmtOpen, setStmtOpen] = useState(false);
  const [stmt, setStmt] = useState(null);

  const load = () => {
    api.get("/customers").then((r) => setRows(r.data)).catch(() => {});
    api.get("/creditbook").then((r) => setCredit(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    if (!q) return rows;
    const s = q.toLowerCase();
    return rows.filter((r) => `${r.name} ${r.type} ${r.town} ${r.phone}`.toLowerCase().includes(s));
  }, [rows, q]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...emptyForm, ...r }); setOpen(true); };
  const ch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const payload = { ...form, credit_limit: Number(form.credit_limit) || 0, balance: Number(form.balance) || 0 };
    try {
      if (editing) await api.put(`/customers/${editing.id}`, payload);
      else await api.post("/customers", payload);
      toast.success(`Customer ${editing ? "updated" : "added"}`);
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openPay = (c) => { setPayFor(c); setPay({ amount: "", type: "payment", method: "cash", note: "" }); setPayOpen(true); };
  const savePay = async () => {
    try {
      await api.post(`/customers/${payFor.id}/payments`, { ...pay, amount: Number(pay.amount) });
      toast.success("Transaction recorded");
      setPayOpen(false); load();
    } catch (e) { toast.error("Failed to record"); }
  };

  const openStmt = async (c) => {
    setStmtOpen(true); setStmt(null);
    const { data } = await api.get(`/customers/${c.id}/statement`);
    setStmt(data);
  };

  return (
    <div className="space-y-5 animate-fade-up">
      <div>
        <h1 className="font-head text-2xl font-bold">Customers & Credit Book</h1>
        <p className="text-muted-foreground text-sm">Manage accounts, credit limits, statements & payment history.</p>
      </div>

      {credit && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="border rounded-lg bg-card p-4"><div className="text-xs text-muted-foreground">Total Customers</div><div className="font-head text-2xl font-bold mt-1">{rows.length}</div></div>
          <div className="border rounded-lg bg-gradient-to-br from-red-50 to-white dark:from-red-950/30 dark:to-slate-900 p-4"><div className="text-xs text-destructive flex items-center gap-1"><CurrencyDollarIcon size={14} weight="duotone"/>Total Outstanding</div><div className="font-head text-2xl font-bold mt-1 text-destructive">{money(credit.total_outstanding)}</div></div>
          <div className="border rounded-lg bg-card p-4"><div className="text-xs text-muted-foreground flex items-center gap-1"><WarningIcon size={14} weight="duotone"/>Over Credit Limit</div><div className="font-head text-2xl font-bold mt-1">{credit.over_limit_count}</div></div>
          <div className="border rounded-lg bg-card p-4"><div className="text-xs text-muted-foreground">Accounts w/ Balance</div><div className="font-head text-2xl font-bold mt-1">{credit.customers.length}</div></div>
        </div>
      )}

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all-customers">All Customers</TabsTrigger>
          <TabsTrigger value="credit" data-testid="tab-credit-book"><BookOpenIcon size={16} className="mr-1.5" />Credit Book</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-3 mt-4">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <MagnifyingGlassIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="customers-search" placeholder="Search customers..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
            </div>
            <Button variant="outline" data-testid="customers-export" onClick={() => exportCSV("customers.csv", filtered)}><DownloadSimpleIcon size={16} className="mr-1.5" />Export</Button>
            <Button data-testid="customers-add" onClick={openNew}><PlusIcon size={16} className="mr-1.5" />Add Customer</Button>
          </div>
          <div className="border rounded-lg bg-card overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-secondary/50">
                <TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Town</TableHead>
                <TableHead>Phone</TableHead><TableHead className="text-right">Credit Limit</TableHead>
                <TableHead className="text-right">Balance</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map((c) => (
                  <TableRow key={c.id} data-testid="customer-row" className="hover:bg-secondary/40">
                    <TableCell className="py-2 font-medium">{c.name}</TableCell>
                    <TableCell className="py-2"><Badge variant="outline" className="text-[11px]">{c.type}</Badge></TableCell>
                    <TableCell className="py-2">{c.town}</TableCell>
                    <TableCell className="py-2 font-mono text-xs">{c.phone}</TableCell>
                    <TableCell className="py-2 text-right">{money(c.credit_limit)}</TableCell>
                    <TableCell className={`py-2 text-right font-semibold ${Number(c.balance) > 0 ? "text-destructive" : ""}`}>{money(c.balance)}</TableCell>
                    <TableCell className="py-2"><StatusBadge value={c.status} /></TableCell>
                    <TableCell className="py-2 text-right whitespace-nowrap">
                      <button onClick={() => openStmt(c)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary" data-testid="customer-statement" title="Statement"><BookOpenIcon size={16} /></button>
                      <button onClick={() => openPay(c)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-emerald-600" data-testid="customer-payment" title="Record payment"><CurrencyDollarIcon size={16} /></button>
                      <button onClick={() => openEdit(c)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary" data-testid="customer-edit"><PencilSimpleIcon size={16} /></button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="credit" className="mt-4">
          <div className="border rounded-lg bg-card overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-secondary/50">
                <TableHead>Customer</TableHead><TableHead>Type</TableHead>
                <TableHead className="text-right">Credit Limit</TableHead><TableHead className="text-right">Outstanding</TableHead>
                <TableHead>Standing</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {credit?.customers.map((c) => {
                  const over = Number(c.balance) > Number(c.credit_limit) && Number(c.credit_limit) > 0;
                  return (
                    <TableRow key={c.id} className="hover:bg-secondary/40">
                      <TableCell className="py-2 font-medium">{c.name}</TableCell>
                      <TableCell className="py-2">{c.type}</TableCell>
                      <TableCell className="py-2 text-right">{money(c.credit_limit)}</TableCell>
                      <TableCell className="py-2 text-right font-semibold text-destructive">{money(c.balance)}</TableCell>
                      <TableCell className="py-2">{over ? <StatusBadge value="overdue" /> : <StatusBadge value="ok" />}</TableCell>
                      <TableCell className="py-2 text-right">
                        <Button size="sm" variant="outline" onClick={() => openPay(c)} data-testid="creditbook-collect">Record Payment</Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {credit?.customers.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No outstanding balances 🎉</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* add/edit customer */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-head">{editing ? "Edit" : "Add"} Customer</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div className="sm:col-span-2"><Label className="text-xs text-muted-foreground mb-1.5 block">Name</Label><Input data-testid="cust-name" value={form.name} onChange={(e) => ch("name", e.target.value)} /></div>
            <div><Label className="text-xs text-muted-foreground mb-1.5 block">Type</Label>
              <Select value={form.type} onValueChange={(v) => ch("type", v)}><SelectTrigger data-testid="cust-type"><SelectValue /></SelectTrigger><SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="text-xs text-muted-foreground mb-1.5 block">Status</Label>
              <Select value={form.status} onValueChange={(v) => ch("status", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent></Select>
            </div>
            <div><Label className="text-xs text-muted-foreground mb-1.5 block">Phone</Label><Input value={form.phone} onChange={(e) => ch("phone", e.target.value)} /></div>
            <div><Label className="text-xs text-muted-foreground mb-1.5 block">Email</Label><Input value={form.email} onChange={(e) => ch("email", e.target.value)} /></div>
            <div className="sm:col-span-2"><Label className="text-xs text-muted-foreground mb-1.5 block">Address</Label><Input value={form.address} onChange={(e) => ch("address", e.target.value)} /></div>
            <div><Label className="text-xs text-muted-foreground mb-1.5 block">Town</Label><Input value={form.town} onChange={(e) => ch("town", e.target.value)} /></div>
            <div><Label className="text-xs text-muted-foreground mb-1.5 block">Emergency Contact</Label><Input value={form.emergency_contact} onChange={(e) => ch("emergency_contact", e.target.value)} /></div>
            <div><Label className="text-xs text-muted-foreground mb-1.5 block">Credit Limit (BZ$)</Label><Input data-testid="cust-credit-limit" type="number" value={form.credit_limit} onChange={(e) => ch("credit_limit", e.target.value)} /></div>
            <div><Label className="text-xs text-muted-foreground mb-1.5 block">Opening Balance (BZ$)</Label><Input type="number" value={form.balance} onChange={(e) => ch("balance", e.target.value)} /></div>
            <div className="sm:col-span-2"><Label className="text-xs text-muted-foreground mb-1.5 block">Notes</Label><Input value={form.notes} onChange={(e) => ch("notes", e.target.value)} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="cust-save" onClick={save}>{editing ? "Save Changes" : "Add Customer"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* payment */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-head">Record Transaction — {payFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-sm text-muted-foreground">Current balance: <span className="font-semibold text-destructive">{money(payFor?.balance)}</span></div>
            <div><Label className="text-xs text-muted-foreground mb-1.5 block">Type</Label>
              <Select value={pay.type} onValueChange={(v) => setPay((p) => ({ ...p, type: v }))}><SelectTrigger data-testid="pay-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="payment">Payment received (reduce balance)</SelectItem><SelectItem value="charge">New charge (increase balance)</SelectItem></SelectContent></Select>
            </div>
            <div><Label className="text-xs text-muted-foreground mb-1.5 block">Amount (BZ$)</Label><Input data-testid="pay-amount" type="number" value={pay.amount} onChange={(e) => setPay((p) => ({ ...p, amount: e.target.value }))} /></div>
            <div><Label className="text-xs text-muted-foreground mb-1.5 block">Method</Label>
              <Select value={pay.method} onValueChange={(v) => setPay((p) => ({ ...p, method: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="card">Card</SelectItem><SelectItem value="cheque">Cheque</SelectItem><SelectItem value="transfer">Bank Transfer</SelectItem></SelectContent></Select>
            </div>
            <div><Label className="text-xs text-muted-foreground mb-1.5 block">Note</Label><Input value={pay.note} onChange={(e) => setPay((p) => ({ ...p, note: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button><Button data-testid="pay-save" onClick={savePay} disabled={!pay.amount}>Record</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* statement */}
      <Dialog open={stmtOpen} onOpenChange={setStmtOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-head">Customer Statement</DialogTitle></DialogHeader>
          {!stmt && <div className="py-8 text-center text-muted-foreground">Loading...</div>}
          {stmt && (
            <div className="space-y-4">
              <div className="border rounded-lg p-4 bg-secondary/30">
                <div className="font-head font-semibold text-lg">{stmt.customer.name}</div>
                <div className="text-sm text-muted-foreground">{stmt.customer.type} · {stmt.customer.town}</div>
                <div className="mt-2 flex gap-6 text-sm">
                  <span>Credit Limit: <b>{money(stmt.customer.credit_limit)}</b></span>
                  <span>Balance: <b className="text-destructive">{money(stmt.customer.balance)}</b></span>
                </div>
              </div>
              <div>
                <div className="font-medium text-sm mb-2">Payment History</div>
                <Table><TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Method</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {stmt.payments.map((p) => (
                      <TableRow key={p.id}><TableCell className="py-1.5">{fmtDate(p.created_at)}</TableCell><TableCell className="py-1.5 capitalize">{p.type}</TableCell><TableCell className="py-1.5">{p.method}</TableCell><TableCell className={`py-1.5 text-right ${p.type === "payment" ? "text-emerald-600" : "text-destructive"}`}>{p.type === "payment" ? "-" : "+"}{money(p.amount)}</TableCell></TableRow>
                    ))}
                    {stmt.payments.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-4 text-muted-foreground text-sm">No payments recorded</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
              <div>
                <div className="font-medium text-sm mb-2">Credit Sales</div>
                <Table><TableHeader><TableRow><TableHead>Invoice</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {stmt.sales.map((sa) => <TableRow key={sa.id}><TableCell className="py-1.5 font-mono text-xs">{sa.invoice_no}</TableCell><TableCell className="py-1.5">{fmtDate(sa.created_at)}</TableCell><TableCell className="py-1.5 text-right">{money(sa.total)}</TableCell></TableRow>)}
                    {stmt.sales.length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-4 text-muted-foreground text-sm">No credit sales</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
